// src/data/coverage/profileMeasureBindings.ts
// ============================================================
// ORGANIZATION PROFILE FIELD -> MEASURES IT INFORMS
//
// Profile is the WEAKEST structured tier. It sits below survey and below evidence, so a
// field here only lifts a measure off library default when nothing better covers it.
//
// CORROBORATING fields never win a measure on their own reading of it; they are recorded
// so the override display can show what agreed with what. `corroboratingOnly` marks them.
// In practice ehrVendor and livesUnderVBC are always shadowed in the opening state (28/30
// by the readiness survey, 7 by the board documents), which is the intended behaviour.
//
// FIELD NAME NOTE: the logical keys below are camelCase, but the canonical profile fixture
// (src/fixtures/acmeOrgProfile.ts) keys its columns in snake_case to mirror the `clients`
// table. `profileColumn` carries the real column name so a reader does not have to guess
// which spelling wins.
// ============================================================

export type ProfileFieldKey = 'annualRevenue' | 'ehrVendor' | 'livesUnderVBC';

export interface ProfileFieldBinding {
  key: ProfileFieldKey;
  /** Column name in src/fixtures/acmeOrgProfile.ts and the `clients` table. */
  profileColumn: string;
  /** Display label. Used verbatim inside the coverage source string. */
  label: string;
  measureIds: number[];
  /**
   * True when the field only corroborates a measure another tier is expected to own.
   * Corroborating fields still resolve a measure if genuinely nothing else covers it.
   */
  corroboratingOnly: boolean;
}

export const PROFILE_FIELD_BINDINGS: readonly ProfileFieldBinding[] = [
  {
    key: 'annualRevenue',
    profileColumn: 'annual_revenue',
    label: 'Annual revenue',
    measureIds: [1, 2],
    corroboratingOnly: false,
  },
  {
    key: 'ehrVendor',
    profileColumn: 'ehr_vendor',
    label: 'EHR vendor',
    measureIds: [28, 30],
    corroboratingOnly: true,
  },
  {
    key: 'livesUnderVBC',
    profileColumn: 'lives_under_vbc',
    label: 'Lives under value-based agreements',
    measureIds: [7],
    corroboratingOnly: true,
  },
] as const;

export const PROFILE_BINDING_BY_KEY: Readonly<Record<ProfileFieldKey, ProfileFieldBinding>> =
  PROFILE_FIELD_BINDINGS.reduce(
    (acc, b) => {
      acc[b.key] = b;
      return acc;
    },
    {} as Record<ProfileFieldKey, ProfileFieldBinding>,
  );

/** Every measure any profile field touches. Deduplicated, ascending. */
export const ALL_PROFILE_MEASURE_IDS: readonly number[] = Array.from(
  new Set(PROFILE_FIELD_BINDINGS.flatMap((b) => b.measureIds)),
).sort((a, b) => a - b);
